// A department's programmes: list, create, rename, remove, and open a
// programme's subjects. Managing is admin / subadmin / superadmin (server:
// CASL `programme`); everyone else just sees the list.
//
// A programme's department is fixed — it can't be moved (its classrooms
// would move with it). A "single classroom" programme (e.g. DCA) is its own
// classroom: the server creates that classroom together with it.
import { useCallback, useEffect, useState } from "react";
import { AlertCircle, BookOpen, GraduationCap, Loader2, Pencil, Plus, Search, Trash2, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { WorkspaceDialog } from "@/components/WorkspaceDialog";
import { DataPager, usePagedList } from "@/components/DataPager";
import { RowActions } from "@/components/RowActions";
import { TeacherCombobox } from "../ClassRooms/TeacherCombobox";
import { useInstituteLabels } from "@/lib/instituteLabels";
import {
    type DepartmentProfile, type Incharge, type Programme,
    createProgramme, listProgrammes, removeProgramme, updateProgramme, withLabels,
} from "./structureApi";
import { ProgrammeSubjectsDialog } from "./ProgrammeSubjectsDialog";

type Props = {
    department: DepartmentProfile | null;   // null = closed
    canManage: boolean;
    onClose: () => void;
};

export function ProgrammesPanel({ department, canManage, onClose }: Props) {
    const L = useInstituteLabels();
    const [ programmes, setProgrammes ] = useState<Programme[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);
    const [ notice, setNotice ] = useState<string | null>(null);

    // create form
    const [ newName, setNewName ] = useState("");
    const [ newSingle, setNewSingle ] = useState(false);
    const [ newIncharge, setNewIncharge ] = useState<Incharge | null>(null);
    const [ creating, setCreating ] = useState(false);

    // inline edit / remove
    const [ editingId, setEditingId ] = useState<string | null>(null);
    const [ editName, setEditName ] = useState("");
    const [ editSingle, setEditSingle ] = useState(false);
    const [ busyId, setBusyId ] = useState<string | null>(null);
    const [ confirmRemoveId, setConfirmRemoveId ] = useState<string | null>(null);

    const [ subjectsOf, setSubjectsOf ] = useState<Programme | null>(null);

    const list = usePagedList(programmes, (p, q) => p.name.toLowerCase().includes(q), 20);

    const load = useCallback(async () => {
        if (!department) return;
        setLoading(true);
        setError(null);
        try {
            setProgrammes(await withLabels(L, () => listProgrammes(department.departmentName)));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [ department, L ]);

    useEffect(() => {
        setProgrammes([]); setNotice(null); setEditingId(null); setConfirmRemoveId(null);
        setNewName(""); setNewSingle(false); setNewIncharge(null);
        load();
    }, [ department ]);

    if (!department) return null;

    const handleCreate = async () => {
        setCreating(true);
        setError(null);
        setNotice(null);
        try {
            const res = await withLabels(L, () => createProgramme({
                name: newName.trim(),
                departmentName: department.departmentName,
                singleClassroom: newSingle,
                incharge: newSingle ? newIncharge : null,
            }));
            if (res.warning === "CLASSROOM_NOT_CREATED") {
                setNotice(`${L.programme} created, but its ${L.classroom.toLowerCase()} could not be. Add it from the ${L.classrooms} page.`);
            } else if (res.classroom) {
                setNotice(`${L.programme} and its ${L.classroom.toLowerCase()} (${res.classroom.id}) created.`);
            }
            setNewName(""); setNewSingle(false); setNewIncharge(null);
            await load();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (p: Programme) => {
        setEditingId(p.id); setEditName(p.name); setEditSingle(p.singleClassroom); setError(null);
    };

    const handleSaveEdit = async (p: Programme) => {
        const body: Parameters<typeof updateProgramme>[ 0 ] = { id: p.id };
        if (editName.trim() !== p.name) body.name = editName.trim();
        if (editSingle !== p.singleClassroom) body.singleClassroom = editSingle;
        if (Object.keys(body).length === 1) { setEditingId(null); return; }
        setBusyId(p.id);
        setError(null);
        try {
            await withLabels(L, () => updateProgramme(body));
            setEditingId(null);
            await load();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const handleRemove = async (p: Programme) => {
        setBusyId(p.id);
        setError(null);
        try {
            await withLabels(L, () => removeProgramme(p.id));
            setConfirmRemoveId(null);
            await load();
        } catch (err: any) {
            setError(err.message);
            setConfirmRemoveId(null);
        } finally {
            setBusyId(null);
        }
    };

    const prog = L.programme.toLowerCase();
    const cls = L.classroom.toLowerCase();

    return (
        <>
            <WorkspaceDialog
                icon={GraduationCap}
                title={`${L.programmes} · ${department.displayName === department.departmentName ? department.departmentName.replace(/_/g, " ") : department.displayName}`}
                description={`A ${prog} belongs to this ${L.department.toLowerCase()} for good; its ${L.classrooms.toLowerCase()} and ${L.subjects.toLowerCase()} hang under it.`}
                footer={<DataPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} loading={loading} />}
                onClose={onClose}
            >
                <div className="grid gap-6 lg:grid-cols-3">
                    {/* LIST */}
                    <div className={canManage ? "space-y-3 lg:col-span-2" : "space-y-3 lg:col-span-3"}>
                        {error && (
                            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                            </div>
                        )}
                        {notice && (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</div>
                        )}

                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input className="h-9 pl-9" placeholder={`Search ${L.programmes.toLowerCase()}…`}
                                value={list.query} onChange={(e) => list.setQuery(e.target.value)} />
                        </div>

                        {loading && programmes.length === 0 ? (
                            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                        ) : list.total === 0 ? (
                            <p className="py-16 text-center text-sm text-muted-foreground">
                                {programmes.length === 0 ? `No ${L.programmes.toLowerCase()} yet.` : `No ${L.programmes.toLowerCase()} match “${list.query}”.`}
                            </p>
                        ) : (
                            <div className="overflow-hidden rounded-lg border">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/30">
                                            <TableHead className="pl-4">{L.programme}</TableHead>
                                            <TableHead className="w-44">Type</TableHead>
                                            <TableHead className="w-12 pr-3"><span className="sr-only">Actions</span></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {list.rows.map((p) => editingId === p.id ? (
                                            <TableRow key={p.id} className="bg-muted/20">
                                                <TableCell className="pl-4" colSpan={3}>
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <Input className="h-8 min-w-48 flex-1" value={editName} maxLength={100}
                                                            onChange={(e) => setEditName(e.target.value)}
                                                            onKeyDown={(e) => e.key === "Enter" && handleSaveEdit(p)} autoFocus />
                                                        <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                                                            <Switch checked={editSingle} onCheckedChange={setEditSingle} /> Single {cls}
                                                        </label>
                                                        <Button size="sm" disabled={busyId === p.id || !editName.trim()} onClick={() => handleSaveEdit(p)} className="gap-1">
                                                            {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : confirmRemoveId === p.id ? (
                                            <TableRow key={p.id} className="bg-red-50/60">
                                                <TableCell className="pl-4" colSpan={3}>
                                                    <div className="flex flex-wrap items-center gap-3 text-sm">
                                                        <span className="flex-1">Remove <b>{p.name}</b>? Only possible while no {cls} belongs to it.</span>
                                                        <Button size="sm" variant="destructive" disabled={busyId === p.id} onClick={() => handleRemove(p)}>
                                                            {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Remove"}
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => setConfirmRemoveId(null)}>Cancel</Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <TableRow key={p.id} className="cursor-pointer" onClick={() => setSubjectsOf(p)}>
                                                <TableCell className="pl-4 font-medium">{p.name}</TableCell>
                                                <TableCell>
                                                    {p.singleClassroom
                                                        ? <Badge variant="secondary" className="text-[11px]">Single {cls}</Badge>
                                                        : <span className="text-xs text-muted-foreground">{L.classrooms}</span>}
                                                </TableCell>
                                                <TableCell className="pr-3 text-right">
                                                    <RowActions label={p.name} busy={busyId === p.id} actions={[
                                                        { label: L.subjects, icon: BookOpen, onSelect: () => setSubjectsOf(p) },
                                                        { label: "Rename / change type", icon: Pencil, onSelect: () => startEdit(p), hidden: !canManage },
                                                        { label: `Remove ${prog}`, icon: Trash2, onSelect: () => setConfirmRemoveId(p.id), destructive: true, hidden: !canManage },
                                                    ]} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>

                    {/* CREATE */}
                    {canManage && (
                        <div className="h-fit space-y-4 rounded-lg border bg-muted/20 p-4">
                            <p className="text-sm font-semibold">New {prog}</p>
                            <div className="space-y-1.5">
                                <Label htmlFor="new-programme-name">Name</Label>
                                <Input id="new-programme-name" placeholder="e.g. B.Tech CE, DCA" value={newName} maxLength={100}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && newName.trim() && !creating && handleCreate()} />
                            </div>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-sm font-medium">Single {cls}</p>
                                    <p className="text-[11px] text-muted-foreground">
                                        The {prog} is one {cls} (like DCA); it is created with it.
                                    </p>
                                </div>
                                <Switch checked={newSingle} onCheckedChange={setNewSingle} />
                            </div>
                            {newSingle && (
                                <div className="space-y-1.5">
                                    <Label>{L.incharge} <span className="text-muted-foreground">(optional)</span></Label>
                                    <TeacherCombobox
                                        value={newIncharge ? `${newIncharge.name} (${newIncharge.eid})` : ""}
                                        placeholder="Search faculty…"
                                        onSelect={(t) => setNewIncharge({ uid: t.uid, name: t.name, eid: t.employee_id, email: t.email })}
                                    />
                                </div>
                            )}
                            <Button onClick={handleCreate} disabled={creating || !newName.trim()} className="w-full gap-1.5">
                                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create {L.programme}
                            </Button>
                        </div>
                    )}
                </div>
            </WorkspaceDialog>

            <ProgrammeSubjectsDialog programme={subjectsOf} canManage={canManage} onClose={() => setSubjectsOf(null)} />
        </>
    );
}
